import { FC, useEffect, useMemo, useState } from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from "@material-ui/icons";
import { isEqual } from "lodash";
import moment from "moment";
import { usePostHog } from "posthog-js/react";

import { Button, ErrorMessage, Modal, WarningMessage } from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { usePrevious } from "hooks";
import {
  formatAmount,
  formatCurrencyAmount,
  formatDate,
  formatDateRange,
  formatNumber,
} from "lib/format";
import { parseNumber } from "lib/parse";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import {
  getBuyNowPriceErrorMessage,
  removeBuyNowPrice,
  setBuyNowPrice,
} from "services/listings";
import { BaseAmountSource, ResaleInfo } from "services/listings/types";
import { ListingOffer } from "services/offers/types";
import { Track } from "services/valuation/types";
import theme from "theme";
import { circleIconSize } from "theme/constants";

import EditBuyNowPriceForm, { FormValues } from "./EditBuyNowPriceForm";

interface EditBuyNowPriceModalProps {
  open: boolean;
  onClose: () => void;
  marketplaceMedianSource: BaseAmountSource;
  marketplaceMedianMultiplier: number;
  marketplaceMedian: string | null;
  buyNowPrice: number;
  dollarAge: number;
  highestOpenBuyerOfferAmount: number;
  isAcceptingFinalOffersEnabled: boolean;
  isMarketplaceMedianDisabled: boolean;
  listingId: number;
  lte: number;
  ltm: number;
  minimumPrice: number;
  offers: ListingOffer[];
  resaleInfo: ResaleInfo | null;
  royxTake: number;
  termExpirationDate: string | null;
  trackList: Track[];
}

const titleByStep = [
  "Edit List Price",
  "Confirm Edit to List Price",
  "List Price Edited",
  "Remove List Price",
  "List Price Removed",
];

const EditBuyNowPriceModal: FC<EditBuyNowPriceModalProps> = ({
  open,
  onClose,
  marketplaceMedianSource,
  marketplaceMedianMultiplier,
  marketplaceMedian,
  buyNowPrice,
  dollarAge,
  highestOpenBuyerOfferAmount,
  isAcceptingFinalOffersEnabled,
  isMarketplaceMedianDisabled,
  listingId,
  lte,
  ltm,
  minimumPrice,
  offers,
  resaleInfo,
  royxTake,
  termExpirationDate,
  trackList,
}) => {
  const initialEditBuyNowPriceValues = {
    netProceeds: String(buyNowPrice * (1 - royxTake)),
    buyNowPrice: String(buyNowPrice),
    royxTakeAmount: String(buyNowPrice * royxTake),
  };
  const {
    handleRefreshListing,
    isLoading: isLoadingAssetDetailData,
    listing,
  } = useAssetDetailData(listingId);
  const posthog = usePostHog();

  const [step, setStep] = useState(0);
  const [values, setValues] = useState(initialEditBuyNowPriceValues);
  const [statisticsChanged, setStatisticsChanged] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const statistics = useMemo(
    () => ({
      lte,
      tracklistLength: trackList.length,
      buyNowPrice,
      offers,
    }),
    [lte, trackList, buyNowPrice, offers],
  );
  const previousStatistics = usePrevious(statistics);

  useEffect(() => {
    if (!!previousStatistics && !isEqual(statistics, previousStatistics)) {
      setStatisticsChanged(true);
    }
  }, [statistics, previousStatistics]);

  const makeOnBackHandler = () => {
    if ([1, 3].includes(step)) {
      return () => {
        setStep(0);
      };
    } else {
      return undefined;
    }
  };

  const handleEditBuyNowPrice = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      await setBuyNowPrice(
        listingId,
        {
          buy_now_price: parseNumber(values.buyNowPrice),
        },
        posthog,
      );
      setStep(s => s + 1);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? getBuyNowPriceErrorMessage(error)
          : DEFAULT_ERROR_MESSAGE,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveBuyNowPrice = async () => {
    try {
      setIsLoading(true);
      await removeBuyNowPrice(listingId, posthog);
      setStep(s => s + 1);
    } catch (e) {
      // pass
    } finally {
      setIsLoading(false);
    }
  };
  const handleSave = async (values: FormValues) => {
    setValues(values);
    setErrorMessage("");
    await handleRefreshListing();
    setStep(s => s + 1);
  };

  return (
    <>
      <Modal
        title={titleByStep[step]}
        open={open}
        onClose={onClose}
        onBack={makeOnBackHandler()}
      >
        {step === 0 && (
          <EditBuyNowPriceForm
            highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
            initialValues={values}
            isAcceptingFinalOffersEnabled={isAcceptingFinalOffersEnabled}
            isMarketplaceMedianDisabled={isMarketplaceMedianDisabled}
            listingKind={listing?.kind}
            marketplaceMedian={marketplaceMedian}
            marketplaceMedianMultiplier={marketplaceMedianMultiplier}
            marketplaceMedianSource={marketplaceMedianSource}
            minimumPrice={minimumPrice}
            onRemoveBuyNowPrice={() => setStep(3)}
            onSave={handleSave}
            resaleInfo={resaleInfo}
            royxTake={royxTake}
          />
        )}
        {step === 1 && (
          <>
            <Typography component="div" gutterBottom>
              <Box fontSize={13} textAlign="center">
                Your listing will be immediately updated.
              </Box>
            </Typography>
            <Box m={2} textAlign="center">
              <Box fontSize={18} m={1}>
                List Price
              </Box>
              <Box
                fontSize={18}
                fontWeight={theme.typography.fontWeightMedium}
                m={1}
              >
                {formatCurrencyAmount("USD", parseNumber(values.buyNowPrice))}
              </Box>
            </Box>
            <Box m={2} textAlign="center">
              <Box fontSize={18} m={1}>
                Your Sale Proceeds
              </Box>
              <Box
                fontSize={18}
                fontWeight={theme.typography.fontWeightMedium}
                m={1}
              >
                {formatCurrencyAmount("USD", parseNumber(values.netProceeds))}
              </Box>
            </Box>
            <Divider />
            <Box m={2} textAlign="center">
              <Typography component="div">
                {statisticsChanged && (
                  <WarningMessage
                    title="Statistics Changed"
                    description={
                      "The earning statistics have changed for " +
                      "this catalog. Please check to confirm before " +
                      "proceeding."
                    }
                  />
                )}
                {errorMessage && (
                  <Box my={2}>
                    <ErrorMessage
                      title="Request Failed"
                      description={errorMessage}
                    />
                  </Box>
                )}
                <Box fontSize={13} textAlign="center" m={2}>
                  Confirmed Earning Statistics
                </Box>
                <Box m={2}>
                  <Box fontSize={15}>Earns Through</Box>
                  <Box
                    fontSize={15}
                    fontWeight={theme.typography.fontWeightMedium}
                  >
                    {formatDate(termExpirationDate)}
                  </Box>
                </Box>
                <Box m={2}>
                  <Box fontSize={15}>Last 12 Months&apos; Earnings</Box>
                  <Box
                    fontSize={15}
                    fontWeight={theme.typography.fontWeightMedium}
                  >
                    {formatAmount(ltm)}
                  </Box>
                </Box>
                <Box m={2}>
                  <Box fontSize={15}>Dollar Age</Box>
                  <Box
                    fontSize={15}
                    fontWeight={theme.typography.fontWeightMedium}
                  >
                    {formatNumber(dollarAge, " Years")}
                  </Box>
                </Box>
              </Typography>
            </Box>
            <Box m={2} textAlign="center">
              <Typography component="div">
                <Box fontSize={10} textAlign="center" m={1}>
                  By clicking below, you acknowledge that you are submitting a
                  binding offer to sell the asset at the price submitted. Your
                  offer shall remain binding until changed or revoked by you.
                </Box>
              </Typography>
              <Box m={2}>
                <Button
                  customColor="green"
                  variant="contained"
                  size="large"
                  id="btn-confirm-modal"
                  fullWidth
                  onClick={handleEditBuyNowPrice}
                  disabled={isLoadingAssetDetailData || isLoading}
                  isLoading={isLoading}
                >
                  Confirm Edit
                </Button>
              </Box>
            </Box>
          </>
        )}
        {step === 2 && (
          <>
            <Typography component="div">
              <Box textAlign="center">
                <CheckCircleIcon
                  style={{
                    fontSize: circleIconSize.big,
                    color: theme.custom.palette.green.main,
                  }}
                />
              </Box>
              <Box fontSize={15} textAlign="center" m={2}>
                Your price has been successfully edited.
              </Box>
            </Typography>
          </>
        )}
        {step === 3 && (
          <>
            <Typography component="div" gutterBottom>
              <Box fontSize={13}>
                {isAcceptingFinalOffersEnabled
                  ? "You'll still receive offers to buy this asset, but there will be no Offer List option."
                  : "You'll still receive offers to buy this asset, but there will be no Buy Now option."}
              </Box>
            </Typography>
            <Box m={2} textAlign="center">
              <Typography component="div">
                <Box m={2}>
                  <Box fontSize={15}>Term Remaining</Box>
                  <Box
                    fontSize={15}
                    fontWeight={theme.typography.fontWeightMedium}
                  >
                    {formatDateRange(termExpirationDate, moment().unix())}
                  </Box>
                </Box>
                <Box m={2}>
                  <Box fontSize={15}>Current List Price</Box>
                  <Box
                    fontSize={15}
                    fontWeight={theme.typography.fontWeightMedium}
                  >
                    {formatCurrencyAmount("USD", buyNowPrice)}
                  </Box>
                </Box>
                <Box m={2}>
                  <Box fontSize={15}>Last 12 Months&apos; Earnings</Box>
                  <Box
                    fontSize={15}
                    fontWeight={theme.typography.fontWeightMedium}
                  >
                    {formatAmount(ltm)}
                  </Box>
                </Box>
                <Box m={2}>
                  <Box fontSize={15}>Dollar Age</Box>
                  <Box
                    fontSize={15}
                    fontWeight={theme.typography.fontWeightMedium}
                  >
                    {formatNumber(dollarAge, " Years")}
                  </Box>
                </Box>
              </Typography>
            </Box>
            <Box m={2} textAlign="center">
              <Box m={2}>
                <Button
                  customColor="red"
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleRemoveBuyNowPrice}
                  disabled={isLoadingAssetDetailData || isLoading}
                  isLoading={isLoading}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          </>
        )}
        {step === 4 && (
          <>
            <Typography component="div">
              <Box textAlign="center">
                <CancelIcon
                  style={{
                    fontSize: circleIconSize.big,
                    color: theme.custom.palette.red.main,
                  }}
                />
              </Box>
              <Box fontSize={15} textAlign="center" m={2}>
                Your price has been successfully removed.
              </Box>
            </Typography>
          </>
        )}
      </Modal>
    </>
  );
};

export default EditBuyNowPriceModal;
